import React, { useState } from 'react';
import { UserProfile, LegacyWisdom, LegacyExecutor } from '../types';
import { generateLifeWisdom } from '../services/geminiService';
import { Scroll, Shield, UserCheck, Archive, Trash2, HeartHandshake, Loader2, Feather, Lock, Eye, Save, Sparkles } from 'lucide-react';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

const LegacyHub: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState<'wisdom' | 'will'>('wisdom');
  const [loadingWisdom, setLoadingWisdom] = useState(false);
  const [executorForm, setExecutorForm] = useState<Partial<LegacyExecutor>>({
      name: user.legacyProfile.executor?.name || '',
      relation: user.legacyProfile.executor?.relation || '',
      email: user.legacyProfile.executor?.email || '',
      accessCode: user.legacyProfile.executor?.accessCode || ''
  });

  const handleGenerateWisdom = async () => {
    setLoadingWisdom(true);
    try {
      const wisdom = await generateLifeWisdom(user);
      if (wisdom) {
        onUpdateUser({
          ...user,
          legacyProfile: {
            ...user.legacyProfile,
            wisdom: wisdom
          }
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingWisdom(false);
    }
  };

  const handleSaveExecutor = () => {
      if (executorForm.name && executorForm.email) {
          onUpdateUser({
              ...user,
              legacyProfile: {
                  ...user.legacyProfile,
                  executor: {
                      name: executorForm.name || '',
                      relation: executorForm.relation || '',
                      email: executorForm.email || '',
                      accessCode: executorForm.accessCode || Math.random().toString(36).slice(-6).toUpperCase(),
                      isVerified: true
                  }
              }
          });
          alert('امین دیجیتال با موفقیت ثبت شد.');
      }
  };

  const updatePostMortemAction = (action: 'delete' | 'archive' | 'memorial') => {
      onUpdateUser({
          ...user,
          legacyProfile: {
              ...user.legacyProfile,
              postMortemAction: action
          }
      });
  };

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20">
      <div className="flex items-center justify-between mb-6">
        <div>
           <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
             <Scroll className="w-6 h-6 text-amber-700" />
             میراث ماندگار
           </h2>
           <p className="text-slate-500 text-sm mt-1">حکمت زندگی و وصیت دیجیتال شما</p>
        </div>
        <div className="flex bg-white rounded-xl border border-slate-200 p-1">
            <button 
                onClick={() => setActiveTab('wisdom')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'wisdom' ? 'bg-amber-100 text-amber-900' : 'text-slate-500'}`}>
                حکمت زندگی
            </button>
            <button 
                onClick={() => setActiveTab('will')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'will' ? 'bg-amber-100 text-amber-900' : 'text-slate-500'}`}>
                وصیت دیجیتال
            </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'wisdom' && (
          <div className="space-y-6">
             {!user.legacyProfile.wisdom ? (
                 <div className="bg-amber-50 rounded-3xl p-8 text-center border border-amber-100">
                     <Feather className="w-16 h-16 text-amber-300 mx-auto mb-4" />
                     <h3 className="text-xl font-bold text-amber-900 mb-2">هنوز سند حکمتی ثبت نشده است</h3>
                     <p className="text-amber-800/70 mb-8 max-w-md mx-auto">
                         با استفاده از هوش مصنوعی، می‌توانیم تمام تجربیات، خاطرات و درس‌های شما را تحلیل کنیم و یک نامه زیبا برای آیندگان بنویسیم.
                     </p>
                     <button 
                        onClick={handleGenerateWisdom}
                        disabled={loadingWisdom}
                        className="bg-amber-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-amber-700 transition-colors flex items-center gap-2 mx-auto shadow-lg shadow-amber-600/20"
                     >
                         {loadingWisdom ? <Loader2 className="animate-spin" /> : <Sparkles className="w-5 h-5" />}
                         تولید سند حکمت زندگی
                     </button>
                 </div>
             ) : (
                 <div className="bg-[#fdfbf7] p-8 rounded-3xl shadow-sm border border-stone-200 relative overflow-hidden">
                     <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-200 to-stone-300"></div>
                     
                     <div className="flex justify-between items-start mb-6">
                         <div>
                             <h3 className="font-serif text-2xl font-bold text-stone-800">خلاصه حکمت {user.name}</h3>
                             <p className="text-xs text-stone-500 mt-1">آخرین بروزرسانی: {new Date(user.legacyProfile.wisdom.lastUpdated).toLocaleDateString('fa-IR')}</p>
                         </div>
                         <button onClick={handleGenerateWisdom} className="text-stone-400 hover:text-stone-600">
                             <RefreshCwIcon className="w-5 h-5" />
                         </button>
                     </div>

                     <div className="prose prose-stone leading-loose text-stone-700 mb-8 font-serif text-justify whitespace-pre-wrap">
                         {user.legacyProfile.wisdom.content}
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-stone-200 pt-6">
                         <div>
                             <h4 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                                 <HeartHandshake className="w-4 h-4 text-emerald-600" /> درس‌های مهم
                             </h4>
                             <ul className="text-sm text-stone-600 space-y-2 list-disc list-inside marker:text-emerald-300">
                                 {user.legacyProfile.wisdom.sections.lessons.map((l, i) => <li key={i}>{l}</li>)}
                             </ul>
                         </div>
                         <div>
                             <h4 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                                 <Shield className="w-4 h-4 text-red-500" /> اشتباهات
                             </h4>
                             <ul className="text-sm text-stone-600 space-y-2 list-disc list-inside marker:text-red-300">
                                 {user.legacyProfile.wisdom.sections.mistakes.map((l, i) => <li key={i}>{l}</li>)}
                             </ul>
                         </div>
                         <div>
                             <h4 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                                 <Feather className="w-4 h-4 text-blue-500" /> توصیه‌ها
                             </h4>
                             <ul className="text-sm text-stone-600 space-y-2 list-disc list-inside marker:text-blue-300">
                                 {user.legacyProfile.wisdom.sections.advice.map((l, i) => <li key={i}>{l}</li>)}
                             </ul>
                         </div>
                     </div>
                 </div>
             )}
          </div>
        )}

        {activeTab === 'will' && (
          <div className="space-y-8">
              {/* Data Handling */}
              <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                  <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <Archive className="w-5 h-5 text-slate-500" />
                      سرنوشت داده‌ها
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button 
                        onClick={() => updatePostMortemAction('delete')}
                        className={`p-4 rounded-xl border text-right transition-all ${user.legacyProfile.postMortemAction === 'delete' ? 'border-red-500 bg-red-50 ring-2 ring-red-200' : 'border-slate-200 hover:bg-slate-50'}`}
                      >
                          <Trash2 className={`w-6 h-6 mb-2 ${user.legacyProfile.postMortemAction === 'delete' ? 'text-red-500' : 'text-slate-400'}`} />
                          <div className="font-bold text-sm text-slate-800">حذف کامل</div>
                          <p className="text-xs text-slate-500 mt-1">تمام داده‌ها پس از تایید فوت پاک شوند.</p>
                      </button>

                      <button 
                        onClick={() => updatePostMortemAction('archive')}
                        className={`p-4 rounded-xl border text-right transition-all ${user.legacyProfile.postMortemAction === 'archive' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 hover:bg-slate-50'}`}
                      >
                          <Lock className={`w-6 h-6 mb-2 ${user.legacyProfile.postMortemAction === 'archive' ? 'text-blue-500' : 'text-slate-400'}`} />
                          <div className="font-bold text-sm text-slate-800">آرشیو خصوصی</div>
                          <p className="text-xs text-slate-500 mt-1">داده‌ها فقط برای امین دیجیتال قابل دانلود باشند.</p>
                      </button>

                      <button 
                        onClick={() => updatePostMortemAction('memorial')}
                        className={`p-4 rounded-xl border text-right transition-all ${user.legacyProfile.postMortemAction === 'memorial' ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' : 'border-slate-200 hover:bg-slate-50'}`}
                      >
                          <HeartHandshake className={`w-6 h-6 mb-2 ${user.legacyProfile.postMortemAction === 'memorial' ? 'text-purple-500' : 'text-slate-400'}`} />
                          <div className="font-bold text-sm text-slate-800">یادبود دیجیتال</div>
                          <p className="text-xs text-slate-500 mt-1">پروفایل به حالت یادبود (Read-Only) درآید.</p>
                      </button>
                  </div>
              </div>

              {/* Executor Setup */}
              <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                  <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <UserCheck className="w-5 h-5 text-emerald-600" />
                      امین دیجیتال (Executor)
                  </h3>
                  <p className="text-sm text-slate-500 mb-6">
                      شخصی که پس از شما، مسئولیت مدیریت داده‌هایتان را بر عهده خواهد داشت. این فرد تنها با کد دسترسی مخصوص می‌تواند وارد شود.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                          <label className="text-xs text-slate-400 block mb-1">نام کامل</label>
                          <input 
                            value={executorForm.name}
                            onChange={(e) => setExecutorForm({...executorForm, name: e.target.value})}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100"
                          />
                      </div>
                      <div>
                          <label className="text-xs text-slate-400 block mb-1">نسبت</label>
                          <input 
                            value={executorForm.relation}
                            onChange={(e) => setExecutorForm({...executorForm, relation: e.target.value})}
                            placeholder="مثلاً: همسر، فرزند"
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100"
                          />
                      </div>
                      <div className="md:col-span-2">
                          <label className="text-xs text-slate-400 block mb-1">ایمیل یا شماره تماس</label>
                          <input 
                            value={executorForm.email}
                            onChange={(e) => setExecutorForm({...executorForm, email: e.target.value})}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100"
                          />
                      </div>
                  </div>

                  {user.legacyProfile.executor && user.legacyProfile.executor.accessCode && (
                      <div className="bg-slate-800 text-white p-4 rounded-xl flex justify-between items-center mb-6">
                          <div className="flex items-center gap-3">
                              <Lock className="w-5 h-5 text-emerald-400" />
                              <div className="flex flex-col">
                                  <span className="text-xs text-slate-400">کد دسترسی امین</span>
                                  <span className="font-mono text-lg tracking-widest">{user.legacyProfile.executor.accessCode}</span>
                              </div>
                          </div>
                          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">محرمانه</span>
                      </div>
                  )}

                  <button 
                    onClick={handleSaveExecutor}
                    className="w-full py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2"
                  >
                      <Save className="w-4 h-4" />
                      {user.legacyProfile.executor ? 'بروزرسانی مشخصات امین' : 'ثبت امین دیجیتال'}
                  </button>
              </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple Icon Component for Refresh
const RefreshCwIcon = (props: any) => (
    <svg 
      {...props}
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
);

export default LegacyHub;